import { Component, OnInit, OnDestroy } from '@angular/core';
import { AdminOverrideBannerComponent } from '@features/shopping/admin-override/admin-override-banner/admin-override-banner.component';
import { AssetCardComponent } from '@features/shopping/asset/card/asset-card.component';
import { AddOnFeaturesComponent } from '@features/shopping/customize-service/add-on-features/add-on-features.component';
import { BusinessPlansComponent } from '@features/shopping/customize-service/business-plans/business-plans.component';
import { BusinessPlansParam } from '@models/customize-service.model';
import { calculateItemsToDelete } from '@models/functions.model';
import { CartInfo } from '@models/session-details';
import { ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import { CART_ITEM_TYPE_FEATURE, CART_ITEM_TYPE_PLAN } from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { CustomizeService } from '@services/customize-service/customize.service';
import { ModalService } from '@services/modal/modal.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { PriceSummaryComponent } from '@shared-components/price-summary/price-summary.component';
import { Subject, Subscription } from 'rxjs';
import { DataLoaderService } from 'tangoe-design-system';

@Component({
  selector: 'ts-manage-services',
  templateUrl: './manage-services.component.html',
  imports: [
    TranslationPipe,
    AdminOverrideBannerComponent,
    AssetCardComponent,
    BusinessPlansComponent,
    AddOnFeaturesComponent,
    PriceSummaryComponent
  ]
})
export class ManageServicesComponent implements OnInit, OnDestroy {
  shopping_cart: ShoppingCartData;
  extras: { [k: string]: string | number } = {
    number_of_plans: 0,
    number_of_features: 0
  };
  shopping_items_price: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: 0
  };
  refresh_summary: Subject<boolean> = new Subject<boolean>();
  private subscriptions: Subscription = new Subscription();

  constructor(
    private customize_svc: CustomizeService,
    private data_loader_svc: DataLoaderService,
    private modal_svc: ModalService,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,

    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.customize_svc.cartChanges.subscribe((res: ShoppingCartData) => {
        this.shopping_cart = { ...res };
        this.updateQueryParam();
        this.updatePrice();
      })
    );

    this.subscriptions.add(
      this.customize_svc.state['extras']?.subscribe(
        (res: { [k: string]: string | number }) => (this.extras = res)
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get is_admin(): boolean {
    return this.session_svc.getUserProfile()?.admin ?? false;
  }

  get valid_cart(): boolean {
    return Boolean(this.shopping_cart && this.getItemByType(CART_ITEM_TYPE_PLAN));
  }

  get disable_continue_btn(): boolean {
    return (
      this.valid_cart &&
      !this.data_loader_svc.isLoading('device-plans') &&
      !this.data_loader_svc.isLoading('device-plan-features')
    );
  }

  get navigate_page(): string {
    return `shop/${this.shopping_cart?.orderTypeId}/finalize-order`;
  }

  get admin_cart_items(): boolean {
    return CartInfo.cart_data?.shopping_cart_items.some(
      (item: ShoppingCartItem) =>
        [CART_ITEM_TYPE_PLAN, CART_ITEM_TYPE_FEATURE].includes(item.type) &&
        item.additional_info?.admin
    )!;
  }

  getItemByType = (type: string): ShoppingCartItem | undefined =>
    this.shopping_cart.shopping_cart_items.find((item: ShoppingCartItem) => item.type === type);

  getAdminOverride(admin_override: boolean): void {
    const plans_param: BusinessPlansParam = {
      orderTypeId: this.shopping_cart.orderTypeId,
      carrierId: this.shopping_cart.carrier?.id ?? 0,
      lineId: this.shopping_cart.lineId ?? undefined,
      include_admin_options: admin_override,
      ref_device_id: this.shopping_cart.existing_asset!.device!.device_catalog_id!
    };
    this.shopping_cart.device_include_admin_options = admin_override;

    if (!admin_override)
      this.shopping_cart.shopping_cart_items = this.getItemByType(CART_ITEM_TYPE_PLAN)
        ?.additional_info?.admin
        ? []
        : this.shopping_cart.shopping_cart_items.reduce(
            (prv: ShoppingCartItem[], cur: ShoppingCartItem) => {
              return cur.type === CART_ITEM_TYPE_FEATURE &&
                (cur.additional_info?.admin || cur.additional_info?.included)
                ? prv
                : [...prv, cur];
            },
            []
          );
    this.customize_svc.setCart({ ...this.shopping_cart });
    this.customize_svc.setState('plans_param', { ...plans_param });
  }

  confirmCartChange(admin_override: boolean): void {
    this.modal_svc.confirmationDialog(
      {
        title: this.translation_svc.getTranslatedModalLabels()[0],
        message: this.translation_svc.getTranslatedModalLabels()[1],
        confirm_button_text: this.translation_svc.translate('continue'),
        cancel_button_text: this.translation_svc.translate('cancel'),
        onConfirm: () => {
          this.shopping_svc
            .clearSpecifiedCartItems(
              calculateItemsToDelete(this.shopping_cart.shopping_cart_items),
              false
            )
            .subscribe(() => this.getAdminOverride(admin_override));
          this.modal_svc.dismissAll();
        }
      },
      {
        size: 'dialog-centered',
        centered: true,
        backdrop: 'static'
      }
    );
  }

  getShoppingCartData(cart_data: ShoppingCartData): void {
    if (cart_data) {
      this.shopping_cart = cart_data;
      this.shopping_items_price.orderTypeId = this.shopping_cart.orderTypeId;
      this.shopping_items_price.lineId = this.shopping_cart.lineId;

      this.customize_svc.setCart(cart_data);
      this.customize_svc.setState('plans_param', {
        orderTypeId: cart_data.orderTypeId,
        carrierId: cart_data.carrier?.id ?? 0,
        lineId: cart_data.lineId ?? undefined,
        include_admin_options: cart_data.device_include_admin_options,
        ref_device_id: cart_data.existing_asset?.device?.device_catalog_id
      });
    }
  }

  private updateQueryParam(): void {
    this.shopping_cart?.shopping_cart_items.map((i: ShoppingCartItem) => {
      i.edit_link_payload = JSON.stringify({
        number_of_plans: this.extras['number_of_plans'],
        number_of_features: this.extras['number_of_features']
      });
    });
  }

  private updatePrice(): void {
    this.shopping_items_price.shopping_cart_items = this.shopping_cart.shopping_cart_items.map(
      (i: ShoppingCartItem) => ({
        type: i.type,
        id: i.id,
        quantity: i.quantity
      })
    );
    this.refresh_summary.next(true);
  }
}
